// Does an incoming link supersede the session this handset already holds?
//
// ---------------------------------------------------------------------------
// WHY THIS IS ITS OWN FILE
// ---------------------------------------------------------------------------
// It is four lines, and it has been wrong twice in production — both times
// silently, both times by swallowing a link someone had deliberately tapped:
//
//   the job    2026-08  a phone signed in to photograph tapped "Open stock
//                       count on this device" and got the gallery. Fixed by
//                       carrying the mode on the link.
//
//   the store  2026-09  a phone signed in to photograph for one store tapped
//                       "Open capture" in ANOTHER store's admin and got the
//                       first store's pile. The mode check could not catch it:
//                       both links say photograph. The person then worked a
//                       shelf into the wrong store with nothing on screen
//                       disagreeing.
//
// Both were a branch inside a 200-line component that no test could reach
// without a DOM, a fetch stub and a router. So it lives here, takes plain
// values, returns a boolean, and has a test that enumerates the cases.
//
// ---------------------------------------------------------------------------
// THESE ARE HINTS, NOT AUTHORISATION
// ---------------------------------------------------------------------------
// `wanted` comes out of the query string and anyone can type one. It decides
// ONLY whether a token is worth spending. The shop and mode that govern the app
// come from the grant, read out of the database on every request — a forged
// hint can make the handset spend a token it did not need to, and can do
// nothing else. It cannot make a phone count, and it cannot point one at a
// store the grant was not minted for.

// ---------------------------------------------------------------------------
// THE PRODUCT IS THE THIRD DIMENSION, AND ITS ABSENCE MEANS SOMETHING
// ---------------------------------------------------------------------------
// A product link (`p`) binds the session to one product. Same shop, same
// photograph mode — so without comparing `p` the link looked redundant and the
// phone stayed in its shelf session, the third silent swallow.
//
// Unlike `s` and `m`, a missing `p` is NOT "no opinion". Every link minted
// before `p` existed was a shelf link, and every link minted since without one
// still is — so absent reads as "no product", and a product-bound handset
// tapping a plain "Open capture" link is asked to go back to the shelf. That
// cannot re-redeem old links: a shelf session has no product, so absent
// matches it.

// ---------------------------------------------------------------------------
// A BARCODE IS A BINDING TOO, AND IT OUTRANKS THE PRODUCT IT RESOLVES TO
// ---------------------------------------------------------------------------
// Quick Scan mints a link for a NEW barcode (`b`), which has no product yet.
// The server resolves it on the first commit and PINS the grant to that
// product — so from then on the session reports BOTH `barcode` and
// `productId`. Comparing products alone would then read a re-tap of the same
// `b` link (no `p`) as "different product" and spend a token for nothing, and
// would key the camera on an id that changed mid-session.
//
// So a session's binding is its barcode when it has one, and only otherwise
// its product; a link's is `b` when present, else `p`. The two kinds never
// match each other: a product link and a barcode link are different errands
// even when they end on the same product, and they are different grants.

/** Normalised so `123`, `"123"` and absent-vs-null compare as intended. */
const productKey = (v) => (v == null || v === "" ? "" : String(v));

// A NEW-ITEM GRANT HAS NO IDENTITY BUT ITS OWN. Two "No barcode" links mint two
// products, so the binding is the grant id (`n:<grantId>`) and outranks any
// barcode on it. A link with no grant id gets a unique key: it can never equal
// anything, so it is always redeemed.
let anonymous = 0;

/**
 * What a session is bound to, as one comparable string: `n:<grantId>`,
 * `b:<barcode>`, `p:<productId>`, or "" for a shelf session. Exported so App
 * keys the camera on the same identity this file compares — see App.jsx.
 *
 * @param {{productId?: string|number|null, barcode?: string|null, newItem?: boolean, grantId?: string|null}|null} session
 */
export function sessionBinding(session) {
  if (!session) return "";
  if (session.newItem) {
    const g = productKey(session.grantId);
    return g ? `n:${g}` : `n:?${++anonymous}`;
  }
  const b = productKey(session.barcode);
  if (b) return `b:${b}`;
  const p = productKey(session.productId);
  return p ? `p:${p}` : "";
}

/** The same string for what a link claims. `n` wins, then `b`, then `p`. */
export function linkBinding(wanted = {}) {
  if (wanted.newItem) {
    const g = productKey(wanted.grant);
    return g ? `n:${g}` : `n:?${++anonymous}`;
  }
  const b = productKey(wanted.barcode);
  if (b) return `b:${b}`;
  const p = productKey(wanted.product);
  return p ? `p:${p}` : "";
}

/**
 * Does the link name a different product OR BARCODE binding from the
 * session's? Split out because App must also drain the queue on it: the
 * backend binds a commit to the GRANT's product or barcode, so a queued
 * capture committed after the switch would land on the wrong product.
 *
 * @param {{productId?: string|number|null, barcode?: string|null}|null} session
 * @param {{product?: string|null, barcode?: string|null}} wanted
 */
export function productDiffers(session, wanted = {}) {
  if (!session) return false;
  return linkBinding(wanted) !== sessionBinding(session);
}

/**
 * Is the link one whose grant the admin polls (a bound link, or a mobile Quick
 * Scan link: mode `quick`, with no p/b/n/g)? Such a link is always redeemed,
 * and a capture still queued must drain first, because the commit would be
 * filed under the NEW grant.
 */
export function linkNeedsFreshGrant(wanted = {}) {
  return linkBinding(wanted) !== "" || wanted.mode === "quick";
}

/**
 * @param {{shop?: string, mode?: string, productId?: string|number|null, barcode?: string|null}|null} session  what this handset holds
 * @param {{shop?: string|null, mode?: string|null, product?: string|null, barcode?: string|null}} wanted  what the link claims
 * @returns {boolean} true when the link is for a different job, store or product
 */
export function linkSupersedes(session, wanted = {}) {
  // No session: the caller has a plainer branch for that, and answering "yes"
  // here would be a claim about a comparison that never happened.
  if (!session) return false;

  // ⚠️ A MISSING HINT IS NOT A MISMATCH — for shop and mode.
  //
  // Links minted before `s` existed carry no shop, and every link minted by a
  // pre-count build carries no mode. Treating absent as "differs" would
  // re-redeem all of them — spending a token, making a second session and
  // undoing the duplicate-device fix that put the session check first.
  const wrongJob = Boolean(wanted.mode) && wanted.mode !== session.mode;
  const wrongShop = Boolean(wanted.shop) && wanted.shop !== session.shop;
  // A BOUND LINK IS ALWAYS REDEEMED, even for the binding this handset already
  // holds. The admin that minted it asks about THAT grant ("has this phone filed
  // anything yet?"), so a second Quick Scan link for the same barcode that
  // quietly reused the old session would record the commit on a grant nobody is
  // polling, and Quick Scan would wait forever. A bound session is thirty
  // minutes and one target, so the token it spends buys nothing worth keeping.
  // A Quick Scan link carries no binding but is the same kind of errand, so it
  // also beats an older quick session (same mode, same shop, no binding).
  const bound = linkNeedsFreshGrant(wanted);
  return wrongJob || wrongShop || bound || productDiffers(session, wanted);
}
